/**
 * Tables inside a node, and the arithmetic they can do.
 *
 * A table is rows of strings — exactly what the person typed. A cell that starts with `=` is a
 * formula and is *shown* as its result; what is stored stays the formula, so editing the cell
 * gives back what was written, and a synced copy recomputes rather than trusting a stale number.
 *
 * The language is the useful part of a spreadsheet and no more: numbers, `A1` references,
 * `A1:B3` ranges, `+ - * / ^`, parentheses, and SUM, AVERAGE, MIN, MAX, COUNT and ROUND — under
 * their Portuguese names too (SOMA, MÉDIA, MÍNIMO, MÁXIMO, CONT, ARRED), because the people this
 * is for type them that way. Arguments are separated by `;` or `,`, and a decimal comma works
 * wherever it cannot be mistaken for a separator.
 *
 * Errors are values, not exceptions: a cell that cannot be computed shows why (`#REF!`,
 * `#DIV/0!`, `#CIRC!`, `#VALUE!`) and the rest of the table still works.
 */
export const DEFAULT_NUMBER_FORMAT = { decimal: '.' };
function isError(value) {
    return typeof value === 'string';
}
/** `A` → 0, `Z` → 25, `AA` → 26. */
export function columnIndex(letters) {
    let index = 0;
    for (const letter of letters.toUpperCase())
        index = index * 26 + (letter.charCodeAt(0) - 64);
    return index - 1;
}
/** 0 → `A`, 26 → `AA`. The header a table editor shows above each column. */
export function columnName(index) {
    let name = '';
    let rest = index + 1;
    while (rest > 0) {
        const remainder = (rest - 1) % 26;
        name = String.fromCharCode(65 + remainder) + name;
        rest = Math.floor((rest - 1) / 26);
    }
    return name;
}
/**
 * The number a cell holds, if it holds one.
 *
 * Forgiving about how people write numbers — a currency sign, a percent, thousands separators —
 * and strict about everything else, so `12 maçãs` is text and not twelve.
 */
export function parseNumber(text, format = DEFAULT_NUMBER_FORMAT) {
    let value = text.trim().replaceAll(' ', ' ');
    if (value === '')
        return null;
    let percent = false;
    if (value.endsWith('%')) {
        percent = true;
        value = value.slice(0, -1).trim();
    }
    let negative = false;
    if (value.startsWith('-')) {
        negative = true;
        value = value.slice(1).trim();
    }
    value = value.replace(/^(R\$|US\$|\$|€|£)\s*/u, '');
    if (value.startsWith('-')) {
        negative = !negative;
        value = value.slice(1).trim();
    }
    if (!/^[\d.,]+$/.test(value) || !/\d/.test(value))
        return null;
    const lastDot = value.lastIndexOf('.');
    const lastComma = value.lastIndexOf(',');
    let normalised;
    if (lastDot !== -1 && lastComma !== -1) {
        // Both appear: whichever comes last is the decimal point.
        const decimal = lastDot > lastComma ? '.' : ',';
        const thousands = decimal === '.' ? ',' : '.';
        normalised = value.replaceAll(thousands, '').replace(decimal, '.');
    }
    else {
        const separator = lastDot !== -1 ? '.' : lastComma !== -1 ? ',' : null;
        if (separator === null)
            normalised = value;
        else {
            const parts = value.split(separator);
            const grouped = parts.length > 2 ||
                (parts.length === 2 && parts[1]?.length === 3 && separator !== format.decimal);
            if (grouped) {
                if (!parts.slice(1).every((part) => part.length === 3))
                    return null;
                normalised = parts.join('');
            }
            else {
                if (parts.length > 2)
                    return null;
                normalised = parts.join('.');
            }
        }
    }
    const number = Number(normalised);
    if (!Number.isFinite(number))
        return null;
    const signed = negative ? -number : number;
    return percent ? signed / 100 : signed;
}
/** A result written back into a cell: no float noise, the reader's decimal mark. */
export function formatNumber(value, format = DEFAULT_NUMBER_FORMAT) {
    if (Object.is(value, -0))
        return '0';
    const rounded = Math.round(value * 1e6) / 1e6;
    const text = String(rounded);
    return format.decimal === ',' ? text.replace('.', ',') : text;
}
function tokenize(source) {
    const tokens = [];
    let index = 0;
    let depth = 0;
    // A comma is a decimal mark unless it can only be a separator: inside a function's
    // parentheses, in a formula that does not use `;` for that job.
    const semicolons = source.includes(';');
    while (index < source.length) {
        const character = source[index] ?? '';
        if (character === ' ' || character === '\t') {
            index += 1;
            continue;
        }
        if (/\d/.test(character) || (character === '.' && /\d/.test(source[index + 1] ?? ''))) {
            let end = index;
            while (end < source.length && /\d/.test(source[end] ?? ''))
                end += 1;
            const mark = source[end];
            const commaIsDecimal = depth === 0 || semicolons;
            if ((mark === '.' || (mark === ',' && commaIsDecimal)) && /\d/.test(source[end + 1] ?? '')) {
                end += 1;
                while (end < source.length && /\d/.test(source[end] ?? ''))
                    end += 1;
            }
            const value = Number(source.slice(index, end).replace(',', '.'));
            if (!Number.isFinite(value))
                return null;
            tokens.push({ kind: 'number', value });
            index = end;
            continue;
        }
        if (/[A-Za-zÀ-ÿ_]/u.test(character)) {
            let end = index;
            while (end < source.length && /[A-Za-zÀ-ÿ_]/u.test(source[end] ?? ''))
                end += 1;
            const letters = source.slice(index, end);
            let digitsEnd = end;
            while (digitsEnd < source.length && /\d/.test(source[digitsEnd] ?? ''))
                digitsEnd += 1;
            if (digitsEnd > end && /^[A-Za-z]{1,2}$/.test(letters)) {
                tokens.push({
                    kind: 'ref',
                    column: columnIndex(letters),
                    row: Number(source.slice(end, digitsEnd)) - 1,
                });
                index = digitsEnd;
                continue;
            }
            tokens.push({ kind: 'name', value: letters.toUpperCase() });
            index = end;
            continue;
        }
        if (character === ',') {
            tokens.push({ kind: 'op', value: ';' });
            index += 1;
            continue;
        }
        if ('+-*/^():;'.includes(character)) {
            if (character === '(')
                depth += 1;
            if (character === ')')
                depth -= 1;
            tokens.push({ kind: 'op', value: character });
            index += 1;
            continue;
        }
        return null;
    }
    return tokens;
}
const FUNCTION_NAMES = {
    SUM: 'SUM',
    SOMA: 'SUM',
    AVERAGE: 'AVERAGE',
    AVG: 'AVERAGE',
    MEDIA: 'AVERAGE',
    MÉDIA: 'AVERAGE',
    MIN: 'MIN',
    MINIMO: 'MIN',
    MÍNIMO: 'MIN',
    MAX: 'MAX',
    MAXIMO: 'MAX',
    MÁXIMO: 'MAX',
    COUNT: 'COUNT',
    CONT: 'COUNT',
    CONTAR: 'COUNT',
    ROUND: 'ROUND',
    ARRED: 'ROUND',
    ABS: 'ABS',
};
/** Recursive descent over the tokens. `null` for anything that is not a well-formed formula. */
function parse(tokens) {
    let position = 0;
    const peek = () => tokens[position];
    const isOp = (value) => {
        const token = peek();
        return token?.kind === 'op' && token.value === value;
    };
    const expression = () => {
        let left = term();
        while (left !== null && (isOp('+') || isOp('-'))) {
            const operator = peek().value;
            position += 1;
            const right = term();
            if (right === null)
                return null;
            left = { kind: 'binary', operator, left, right };
        }
        return left;
    };
    const term = () => {
        let left = unary();
        while (left !== null && (isOp('*') || isOp('/'))) {
            const operator = peek().value;
            position += 1;
            const right = unary();
            if (right === null)
                return null;
            left = { kind: 'binary', operator, left, right };
        }
        return left;
    };
    const unary = () => {
        if (isOp('-')) {
            position += 1;
            const operand = unary();
            return operand === null ? null : { kind: 'negate', operand };
        }
        if (isOp('+')) {
            position += 1;
            return unary();
        }
        return power();
    };
    const power = () => {
        const base = primary();
        if (base === null)
            return null;
        if (isOp('^')) {
            position += 1;
            const exponent = unary();
            if (exponent === null)
                return null;
            return { kind: 'binary', operator: '^', left: base, right: exponent };
        }
        return base;
    };
    const primary = () => {
        const token = peek();
        if (token === undefined)
            return null;
        if (token.kind === 'number') {
            position += 1;
            return { kind: 'number', value: token.value };
        }
        if (token.kind === 'ref') {
            position += 1;
            if (isOp(':')) {
                position += 1;
                const end = peek();
                if (end?.kind !== 'ref')
                    return null;
                position += 1;
                return {
                    kind: 'range',
                    from: { row: token.row, column: token.column },
                    to: { row: end.row, column: end.column },
                };
            }
            return { kind: 'ref', row: token.row, column: token.column };
        }
        if (token.kind === 'name') {
            const name = FUNCTION_NAMES[token.value];
            position += 1;
            if (name === undefined || !isOp('('))
                return null;
            position += 1;
            const args = [];
            if (!isOp(')')) {
                for (;;) {
                    const argument = expression();
                    if (argument === null)
                        return null;
                    args.push(argument);
                    if (isOp(';')) {
                        position += 1;
                        continue;
                    }
                    break;
                }
            }
            if (!isOp(')'))
                return null;
            position += 1;
            return { kind: 'call', name, args };
        }
        if (isOp('(')) {
            position += 1;
            const inner = expression();
            if (inner === null || !isOp(')'))
                return null;
            position += 1;
            return inner;
        }
        return null;
    };
    const result = expression();
    return result !== null && position === tokens.length ? result : null;
}
/* ------------------------------------------------------------------------------------------ */
/* Evaluation                                                                                  */
/* ------------------------------------------------------------------------------------------ */
/** The most cells a range may cover. A bound, so `A1:ZZ9999` cannot stall a phone. */
const MAX_RANGE_CELLS = 2_000;
/**
 * Every cell as it should be shown: text as typed, formulas as their results.
 *
 * Cells referring to each other are resolved on demand with a visiting set, so a cycle is found
 * and reported as `#CIRC!` in the cells that form it rather than recursing until the stack runs
 * out.
 */
export function evaluateTable(rows, format = DEFAULT_NUMBER_FORMAT) {
    const cache = new Map();
    const visiting = new Set();
    const cellText = (row, column) => rows[row]?.[column];
    /** A referenced cell's value: `null` for text or an empty cell, which functions skip. */
    const valueAt = (row, column) => {
        const text = cellText(row, column);
        if (text === undefined)
            return row < 0 || column < 0 ? '#REF!' : null;
        const key = `${String(row)}:${String(column)}`;
        if (cache.has(key))
            return cache.get(key) ?? null;
        if (!text.trimStart().startsWith('=')) {
            const value = parseNumber(text, format);
            cache.set(key, value);
            return value;
        }
        if (visiting.has(key))
            return '#CIRC!';
        visiting.add(key);
        const value = computeFormula(text);
        visiting.delete(key);
        cache.set(key, value);
        return value;
    };
    const inside = (row, column) => row >= 0 && column >= 0 && row < rows.length && column < (rows[row]?.length ?? 0);
    const collect = (expression) => {
        if (expression.kind !== 'range')
            return [evaluate(expression)];
        const top = Math.min(expression.from.row, expression.to.row);
        const bottom = Math.max(expression.from.row, expression.to.row);
        const left = Math.min(expression.from.column, expression.to.column);
        const right = Math.max(expression.from.column, expression.to.column);
        if ((bottom - top + 1) * (right - left + 1) > MAX_RANGE_CELLS)
            return '#REF!';
        if (!inside(top, left))
            return '#REF!';
        const values = [];
        for (let row = top; row <= bottom; row += 1) {
            for (let column = left; column <= right; column += 1) {
                if (inside(row, column))
                    values.push(valueAt(row, column));
            }
        }
        return values;
    };
    const evaluate = (expression) => {
        switch (expression.kind) {
            case 'number':
                return expression.value;
            case 'ref': {
                if (!inside(expression.row, expression.column))
                    return '#REF!';
                return valueAt(expression.row, expression.column) ?? 0;
            }
            case 'range':
                // A bare range outside a function has no single value.
                return '#VALUE!';
            case 'negate': {
                const value = evaluate(expression.operand);
                return isError(value) ? value : -value;
            }
            case 'binary': {
                const left = evaluate(expression.left);
                if (isError(left))
                    return left;
                const right = evaluate(expression.right);
                if (isError(right))
                    return right;
                switch (expression.operator) {
                    case '+':
                        return left + right;
                    case '-':
                        return left - right;
                    case '*':
                        return left * right;
                    case '/':
                        return right === 0 ? '#DIV/0!' : left / right;
                    case '^': {
                        const result = left ** right;
                        return Number.isFinite(result) ? result : '#VALUE!';
                    }
                }
                return '#VALUE!';
            }
            case 'call':
                return call(expression.name, expression.args);
        }
    };
    const call = (name, args) => {
        if (name === 'ROUND' || name === 'ABS') {
            const first = args[0];
            if (first === undefined)
                return '#VALUE!';
            const value = evaluate(first);
            if (isError(value))
                return value;
            if (name === 'ABS')
                return Math.abs(value);
            const second = args[1];
            const digits = second === undefined ? 0 : evaluate(second);
            if (isError(digits))
                return digits;
            const factor = 10 ** Math.max(0, Math.min(10, Math.trunc(digits)));
            return Math.round(value * factor) / factor;
        }
        const numbers = [];
        for (const argument of args) {
            const values = collect(argument);
            if (typeof values === 'string')
                return values;
            for (const value of values) {
                if (value === null)
                    continue;
                if (isError(value))
                    return value;
                numbers.push(value);
            }
        }
        switch (name) {
            case 'SUM':
                return numbers.reduce((total, value) => total + value, 0);
            case 'AVERAGE':
                return numbers.length === 0
                    ? '#DIV/0!'
                    : numbers.reduce((total, value) => total + value, 0) / numbers.length;
            case 'MIN':
                return numbers.length === 0 ? 0 : Math.min(...numbers);
            case 'MAX':
                return numbers.length === 0 ? 0 : Math.max(...numbers);
            case 'COUNT':
                return numbers.length;
        }
    };
    const computeFormula = (text) => {
        const tokens = tokenize(text.trimStart().slice(1));
        if (tokens === null || tokens.length === 0)
            return '#VALUE!';
        const expression = parse(tokens);
        if (expression === null)
            return '#VALUE!';
        const value = evaluate(expression);
        if (!isError(value) && !Number.isFinite(value))
            return '#VALUE!';
        return value;
    };
    return rows.map((row, rowIndex) => row.map((text, columnIndexValue) => {
        if (!text.trimStart().startsWith('='))
            return text;
        const value = valueAt(rowIndex, columnIndexValue);
        if (value === null)
            return '';
        return isError(value) ? value : formatNumber(value, format);
    }));
}
/** True when the cell is a formula rather than a value. */
export function isFormula(text) {
    return text.trimStart().startsWith('=');
}
//# sourceMappingURL=table.js.map